import { crmGet, fsmGet, fsmPost, fsmPut, fsmPatch, fsmDel, hrmsGet } from "../api/api";
import { normalizePageResponse } from "../lib/pagination";
import sessionDB from "../utils/sessionDB";

/**
 * FSM module data layer — CRM + FSM endpoints.
 *
 * Paths are relative to:
 *   CRM_BASE_URL (`${API_BASE_URL}/saas/crm`)
 *   FSM_BASE_URL (`${API_BASE_URL}/saas/fsm`)
 *
 * Auth headers (Bearer token, X-Tenant-Code, X-Service-Code) are attached
 * automatically by the shared API client in src/api/api.js.
 */

const ENDPOINTS = {
  searchCustomers:   "/customer-info/search",
  searchLocations:   "/customer-locations/search/customer",
  locationById:      (id) => `/customer-locations/${id}`,
  searchContacts:    "/customer-contact/search/customer",
  searchAssets:      "/customer-assets/search",
  searchAssetsByCustomerLocation: "/customer-assets/search/customer-location",
  assetById:         (id) => `/customer-assets/${id}`,
  searchSystems:     "/products-services/search",
  searchSystemsByStatus: "/products-services/search/status",
  searchComplaints:  (productServiceId) =>
    `/products-complaints/product/${productServiceId}`,
  addComplaint:      "/customer-complaints/add",
  searchTickets:     "/customer-complaints/search",
  complaintsByCustomer: (customerId) =>
    `/customer-complaints/customer/${customerId}`,
  complaintById: (complaintId) => `/customer-complaints/${complaintId}`,
  managerStatus: (complaintId) =>
    `/customer-complaints/manager-status/${complaintId}`,
  updateComplaint: (complaintId) =>
    `/customer-complaints/update/${complaintId}`,
  technicianStatus: (complaintId) =>
    `/customer-complaints/technician-status/${complaintId}`,
  scheduleVisit: (complaintId) =>
    `/customer-complaints/schedule/${complaintId}`,
  complaintsByTechnician: (technicianId) =>
    `/customer-complaints/technician/${technicianId}`,
  complaintsByStatus: (complaintStatus) =>
    `/customer-complaints/status/${encodeURIComponent(complaintStatus)}`,
  complaintLog: (complaintId) => `/complaint-log/complaint/${complaintId}`,
  searchChecklistItems: "/products-complaints-checklist/search",
  addChecklistItem: "/products-complaints-checklist/add",
  updateChecklistItem: (id) => `/products-complaints-checklist/update/${id}`,
  deleteChecklistItem: (id) => `/products-complaints-checklist/delete/${id}`,
  checklistById: (id) => `/products-complaints-checklist/${id}`,
  checklistByComplaintType: (complaintTypeId) =>
    `/products-complaints-checklist/complaint/${complaintTypeId}`,
  checklistByWhoToCheck: (complaintTypeId) =>
    `/products-complaints-checklist/complaint/${complaintTypeId}/search/whotocheck`,
  checklistCountByComplaint: (complaintTypeId) =>
    `/products-complaints-checklist/count/complaint/${complaintTypeId}`,
};

const SERVICE_CODES = {
  searchCustomers:  "CRM01S",
  searchLocations:  "CRM04S",
  locationById:     "CRM04S",
  searchContacts:   "CRM02S",
  searchAssets:     "CRM03S",
  assetById:        "CRM03S",
  searchSystems:    "FSM01S",
  searchComplaints: "FSM01S",
  addComplaint:     "FSM03A",
  searchTickets:    "FSM03S",
  complaintsByCustomer: "FSM03S",
  complaintsByStatus: "FSM03S",
  complaintById:    "FSM03S",
  complaintsByTechnician: "FSM03S",
  complaintLog:     "FSM03S",
  managerStatus:    "FSM03U",
  updateComplaint:  "FSM03U",
  technicianStatus: "FSM03U",
  scheduleVisit:    "FSM03U",
  searchTechnicians: "HR02S",
  searchChecklistItems: "FSM01S",
  addChecklistItem: "FSM01A",
  updateChecklistItem: "FSM01U",
  deleteChecklistItem: "FSM01D",
};

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.content)) return response.data.content;
  if (Array.isArray(response?.content)) return response.content;
  return [];
}

function unwrapItem(response) {
  if (response?.data && typeof response.data === "object" && !Array.isArray(response.data)) {
    return response.data;
  }
  return response ?? null;
}

function sessionExpiredMessage(err, fallback) {
  return err?.status === 401
    ? "Session expired. Please refresh and try again."
    : fallback;
}

export { sessionExpiredMessage };

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

/**
 * Maps a CRM customer-info record.
 * Label: companyName  |  Value: customerId
 */
export function mapCustomer(item) {
  const customerId = item?.customerId ?? item?.id;
  const name =
    item?.companyName ??
    item?.customerName ??
    item?.name ??
    (customerId != null ? `Customer #${customerId}` : "Customer");

  return {
    id:         customerId != null ? String(customerId) : "",
    customerId,
    name,
    priority:   item?.priority ?? item?.customerPriority ?? "Standard",
    raw:        item,
  };
}

/** GET /saas/crm/customer-info/search — CRM01S */
export async function searchCustomers({
  page    = 0,
  size,
  sortBy  = "customerId",
  sortDir = "ASC",
  searchTerm,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    sortBy,
    sortDir,
  });
  if (size != null && size !== "") params.set("size", String(size));
  if (searchTerm) params.set("searchTerm", searchTerm);

  const response = await crmGet(
    `${ENDPOINTS.searchCustomers}?${params.toString()}`,
    SERVICE_CODES.searchCustomers
  );

  const items = unwrapList(response).map(mapCustomer).filter((c) => c.id);
  const pageSize = Number(size) || 10;
  const total = Number(
    response?.data?.totalElements ??
      response?.totalElements ??
      response?.data?.total ??
      response?.total ??
      items.length
  ) || items.length;
  const reportedTotalPages = Number(
    response?.data?.totalPages ?? response?.totalPages
  );
  const totalPages = Number.isFinite(reportedTotalPages) && reportedTotalPages > 0
    ? reportedTotalPages
    : Math.max(1, Math.ceil(total / pageSize));
  const hasMore = Number.isFinite(reportedTotalPages) && reportedTotalPages > 0
    ? page + 1 < reportedTotalPages
    : items.length >= pageSize;

  return { items, total, page, size: pageSize, totalPages, hasMore };
}

// ---------------------------------------------------------------------------
// Customer Locations
// ---------------------------------------------------------------------------

/**
 * Maps a CRM customer-location record.
 * Label: locationName (or address)  |  Value: locationId
 */
export function mapLocation(item) {
  const locationId = item?.locationId ?? item?.id;
  const label =
    item?.locationName ??
    item?.siteName ??
    item?.label ??
    item?.address ??
    item?.fullAddress ??
    item?.addressLine1 ??
    (locationId != null ? `Location #${locationId}` : "Location");

  return {
    id:         locationId != null ? String(locationId) : "",
    locationId,
    label,
    raw:        item,
  };
}

/**
 * GET /saas/crm/customer-locations/search/customer?customerId={id}
 * CRM04S
 */
export async function searchCustomerLocations({
  customerId,
  page    = 0,
  size    = 10,
  sortBy  = "locationId",
  sortDir = "ASC",
  searchTerm,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });
  if (customerId != null) params.set("customerId", String(customerId));
  if (searchTerm) params.set("searchTerm", searchTerm);

  const response = await crmGet(
    `${ENDPOINTS.searchLocations}?${params.toString()}`,
    SERVICE_CODES.searchLocations
  );

  return unwrapList(response).map(mapLocation).filter((l) => l.id);
}

/**
 * Maps GET /customer-locations/{locationId} into ticket-view fields.
 */
export function mapLocationDetail(item) {
  if (!item) return null;

  const locationId = item?.locationId ?? item?.id ?? null;
  const locationName = String(item?.locationName ?? "").trim();
  const addressLine1 = String(item?.addressLine1 ?? "").trim();
  const city = String(item?.city ?? "").trim();
  const state = String(item?.state ?? "").trim();
  const siteType = String(item?.siteType ?? "").trim();

  const addressDisplay = [locationName, addressLine1, city, state]
    .filter(Boolean)
    .join(", ");

  return {
    locationId,
    locationName,
    addressLine1,
    addressLine2: String(item?.addressLine2 ?? "").trim(),
    city,
    state,
    siteType,
    addressDisplay: addressDisplay || (locationId != null ? `Location #${locationId}` : ""),
    raw: item,
  };
}

/**
 * GET /saas/crm/customer-locations/{locationId}
 * CRM04S
 */
export async function fetchCustomerLocationById(locationId) {
  if (locationId == null || locationId === "") return mapLocationDetail(null);

  const response = await crmGet(
    ENDPOINTS.locationById(locationId),
    SERVICE_CODES.locationById
  );

  return mapLocationDetail(unwrapItem(response));
}

// ---------------------------------------------------------------------------
// Customer Contacts
// ---------------------------------------------------------------------------

/**
 * Maps a CRM customer-contact record.
 * Label: contact full name  |  Value: contactId
 */
export function mapContact(item) {
  const contactId = item?.contactId ?? item?.id;
  const fullName = [item?.firstName, item?.lastName].filter(Boolean).join(" ").trim();
  const name =
    item?.contactName ??
    item?.fullName ??
    item?.name ??
    (fullName || (contactId != null ? `Contact #${contactId}` : "Contact"));

  return {
    id:        contactId != null ? String(contactId) : "",
    contactId,
    name,
    phone:     item?.phone ?? "",
    email:     item?.email ?? "",
    raw:       item,
  };
}

/** GET /saas/crm/customer-contact/search/customer?customerId={id} — CRM02S */
export async function searchCustomerContacts({
  customerId,
  page    = 0,
  size    = 10,
  sortBy  = "contactId",
  sortDir = "ASC",
  searchTerm,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });
  if (customerId != null) params.set("customerId", String(customerId));
  if (searchTerm) params.set("searchTerm", searchTerm);

  const response = await crmGet(
    `${ENDPOINTS.searchContacts}?${params.toString()}`,
    SERVICE_CODES.searchContacts
  );

  return unwrapList(response).map(mapContact).filter((c) => c.id);
}

// ---------------------------------------------------------------------------
// Products / Systems (FSM)
// ---------------------------------------------------------------------------

/**
 * Maps an FSM products-services record (System dropdown).
 * Label: product/service name  |  Value: productServiceId
 */
export function mapProductService(item) {
  const productServiceId = item?.productServiceId ?? item?.id;
  const name =
    item?.productServiceName ??
    item?.serviceName ??
    item?.systemName ??
    item?.name ??
    (productServiceId != null ? `System #${productServiceId}` : "System");

  return {
    id:               productServiceId != null ? String(productServiceId) : "",
    productServiceId,
    name,
    raw:              item,
  };
}

/**
 * GET /saas/fsm/products-services/search
 * FSM01S
 *
 * In-flight + session cache keyed by query params so React StrictMode remounts
 * (and list → create navigation) do not double-hit the network.
 */
const productServicesCache = new Map();
const productServicesInflight = new Map();

export async function searchProductServices(options = {}) {
  const result = await searchProductServicesPage(options);
  return result.items;
}

export async function searchProductServicesPage({
  page    = 0,
  size    = 10,
  sortBy  = "productServiceId",
  sortDir = "ASC",
} = {}) {
  const cacheKey = `${page}|${size}|${sortBy}|${sortDir}`;
  if (productServicesCache.has(cacheKey)) {
    return productServicesCache.get(cacheKey);
  }
  if (productServicesInflight.has(cacheKey)) {
    return productServicesInflight.get(cacheKey);
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const promise = (async () => {
    try {
      const response = await fsmGet(
        `${ENDPOINTS.searchSystems}?${params.toString()}`,
        SERVICE_CODES.searchSystems
      );
      const records = unwrapList(response);
      const items = records.map(mapProductService).filter((s) => s.id);
      const data = response?.data ?? response;
      const currentPage = Number(data?.number ?? data?.pageNumber ?? page);
      const pageSize = Number(size) || 10;
      const totalPages = data?.totalPages ?? response?.totalPages;
      const total = data?.totalElements ?? response?.totalElements ?? data?.total ?? response?.total;
      const last = data?.last ?? response?.last;
      const hasMore = typeof last === "boolean"
        ? !last
        : totalPages != null
          ? currentPage + 1 < Number(totalPages)
          : total != null
            ? (currentPage + 1) * pageSize < Number(total)
            : records.length >= pageSize;
      const result = { items, page: currentPage, size: pageSize, hasMore };
      productServicesCache.set(cacheKey, result);
      return result;
    } catch (err) {
      productServicesCache.delete(cacheKey);
      throw err;
    } finally {
      productServicesInflight.delete(cacheKey);
    }
  })();

  productServicesInflight.set(cacheKey, promise);
  return promise;
}

/**
 * GET /saas/fsm/products-services/search/status
 * ?isActive=true&page=0&size=10&sortBy=productServiceName&sortDir=ASC
 */
export async function searchProductServicesByStatus({
  isActive = true,
  page = 0,
  size = 10,
  sortBy = "productServiceName",
  sortDir = "ASC",
} = {}) {
  const params = new URLSearchParams({
    isActive: String(!!isActive),
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.searchSystemsByStatus}?${params.toString()}`,
    SERVICE_CODES.searchSystems
  );
  return unwrapList(response).map(mapProductService).filter((s) => s.id);
}

// ---------------------------------------------------------------------------
// Complaints by System (FSM)
// ---------------------------------------------------------------------------

/**
 * Maps an FSM products-complaints record.
 * Label: complaint type name  |  Value: complaintTypeId
 */
export function mapComplaintType(item) {
  const complaintTypeId = item?.complaintTypeId ?? item?.id;
  const name =
    item?.complaintType ??
    item?.complaintName ??
    item?.complaintTypeName ??
    item?.name ??
    (complaintTypeId != null ? `Complaint #${complaintTypeId}` : "Complaint");

  return {
    id:              complaintTypeId != null ? String(complaintTypeId) : "",
    complaintTypeId,
    name,
    raw:             item,
  };
}

/**
 * GET /saas/fsm/products-complaints/product/{productServiceId}
 * FSM01S
 */
export async function searchComplaintsByProduct({
  productServiceId,
  page    = 0,
  size    = 10,
  sortBy  = "complaintTypeId",
  sortDir = "ASC",
} = {}) {
  if (productServiceId == null || productServiceId === "") return [];

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.searchComplaints(productServiceId)}?${params.toString()}`,
    SERVICE_CODES.searchComplaints
  );

  return unwrapList(response).map(mapComplaintType).filter((c) => c.id);
}

// ---------------------------------------------------------------------------
// Checklist Template Index (Products x Complaint Types)
// ---------------------------------------------------------------------------

/**
 * GET /saas/fsm/products-complaints-checklist/count/complaint/{complaintTypeId}
 * FSM01S — number of checklist steps for a complaint type.
 */
export async function fetchChecklistCountByComplaint(complaintTypeId) {
  if (complaintTypeId == null || complaintTypeId === "") {
    return 0;
  }

  const response = await fsmGet(
    ENDPOINTS.checklistCountByComplaint(complaintTypeId),
    SERVICE_CODES.searchChecklistItems
  );

  if (typeof response === "number") return response;
  if (typeof response?.data === "number") return response.data;
  if (typeof response?.count === "number") return response.count;
  if (typeof response?.data?.count === "number") return response.data.count;
  if (typeof response?.total === "number") return response.total;
  if (typeof response?.data?.total === "number") return response.data.total;
  if (typeof response?.data?.totalElements === "number") {
    return response.data.totalElements;
  }

  const n = Number(
    response?.data ?? response?.count ?? response?.total ?? response
  );
  return Number.isFinite(n) ? n : 0;
}

/**
 * Groups flat checklist items into Checklist Templates.
 * Unique key: complaintTypeId + whoToCheck (Checklist Type).
 */
export function groupChecklistItemsIntoTemplates(items = []) {
  const map = new Map();

  for (const item of items) {
    const complaintTypeId = item?.complaintTypeId;
    const whoToCheck = String(item?.whoToCheck || "").trim();
    if (complaintTypeId == null || complaintTypeId === "" || !whoToCheck) {
      continue;
    }

    const key = `${complaintTypeId}::${whoToCheck}`;
    if (!map.has(key)) {
      map.set(key, {
        id: key,
        complaintTypeId,
        complaintTypeName:
          item.complaintTypeName && item.complaintTypeName !== "—"
            ? item.complaintTypeName
            : "—",
        productServiceId: item.productServiceId ?? null,
        productName:
          item.systemName && item.systemName !== "—"
            ? item.systemName
            : "—",
        whoToCheck,
        checklistItems: [],
      });
    }

    const template = map.get(key);
    template.checklistItems.push(item);

    if (item.complaintTypeName && item.complaintTypeName !== "—") {
      template.complaintTypeName = item.complaintTypeName;
    }
    if (item.systemName && item.systemName !== "—") {
      template.productName = item.systemName;
    }
    if (item.productServiceId != null) {
      template.productServiceId = item.productServiceId;
    }
  }

  return Array.from(map.values()).map((template) => {
    const checklistItems = [...template.checklistItems].sort(
      (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
    );
    return {
      ...template,
      checklistItems,
      steps: checklistItems.length,
      stepCount: checklistItems.length,
    };
  });
}

/**
 * Paginates GET /products-complaints-checklist/search
 * optionally scoped by complaintTypeId.
 */
export async function fetchAllChecklistItems({
  complaintTypeId,
  pageSize = 10,
} = {}) {
  const matched = [];
  let page = 0;
  let totalPages = 1;

  while (page < totalPages) {
    const { items, total } = await searchChecklistItems({
      page,
      size: pageSize,
      sortBy: "sequenceOrder",
      sortDir: "ASC",
      complaintTypeId,
    });
    matched.push(...items);
    totalPages = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
    if (items.length === 0) break;
    page += 1;
    if (page > 100) break;
  }

  return matched;
}

/**
 * Builds the Checklist Template index — one row per unique
 * complaintTypeId + whoToCheck that has checklist items.
 *
 * Steps = number of checklist items in the group (NOT the count API).
 *
 * Flow:
 *   1. GET /products-services/search
 *   2. GET /products-complaints/product/{productServiceId}
 *   3. GET /products-complaints-checklist/complaint/{complaintTypeId}  (all pages)
 *   4. Frontend group by whoToCheck within each complaint type
 *   5. Deduplicate by template key across products
 */
export async function fetchChecklistTemplateIndex() {
  const products = await searchProductServices({
    page: 0,
    size: 10,
    sortBy: "productServiceId",
    sortDir: "ASC",
  });

  const complaintMeta = new Map();

  await Promise.all(
    products.map(async (product) => {
      const productServiceId = product.productServiceId ?? product.id;
      const productName = product.name || `System #${productServiceId}`;
      try {
        const complaintTypes = await searchComplaintsByProduct({
          productServiceId,
          page: 0,
          size: 10,
          sortBy: "complaintTypeId",
          sortDir: "ASC",
        });
        for (const ct of complaintTypes) {
          const complaintTypeId = ct.complaintTypeId ?? ct.id;
          if (complaintTypeId == null || complaintTypeId === "") continue;
          complaintMeta.set(String(complaintTypeId), {
            productServiceId,
            productName,
            complaintTypeId,
            complaintTypeName: ct.name,
          });
        }
      } catch (err) {
        console.error(
          `Failed to load complaint types for product ${productServiceId}:`,
          err
        );
      }
    })
  );

  const templateMap = new Map();

  await Promise.all(
    [...complaintMeta.values()].map(async (meta) => {
      try {
        // Path-scoped endpoint — reliable filter by complaint type
        const rawItems = await fetchChecklistItemsByComplaintType(
          meta.complaintTypeId
        );

        // Keep only rows that belong to this complaint type (defensive)
        const items = rawItems
          .filter(
            (item) =>
              item.complaintTypeId == null ||
              String(item.complaintTypeId) === String(meta.complaintTypeId)
          )
          .map((item) => ({
            ...item,
            complaintTypeId: meta.complaintTypeId,
            complaintTypeName: meta.complaintTypeName,
            productServiceId: meta.productServiceId,
            systemName: meta.productName,
          }));

        // Group ONLY by whoToCheck — all items share this complaintTypeId
        const byWho = new Map();
        for (const item of items) {
          const whoToCheck = String(item.whoToCheck || "").trim();
          if (!whoToCheck) continue;
          const whoKey = whoToCheck.toLowerCase();
          if (!byWho.has(whoKey)) {
            byWho.set(whoKey, { whoToCheck, checklistItems: [] });
          }
          byWho.get(whoKey).checklistItems.push(item);
        }

        for (const { whoToCheck, checklistItems } of byWho.values()) {
          const key = `${meta.complaintTypeId}::${whoToCheck}`;
          const sorted = [...checklistItems].sort(
            (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
          );
          // Prefer the larger group if the same key appears twice (dedupe)
          const existing = templateMap.get(key);
          if (existing && existing.checklistItems.length >= sorted.length) {
            continue;
          }
          templateMap.set(key, {
            id: key,
            complaintTypeId: meta.complaintTypeId,
            complaintTypeName: meta.complaintTypeName,
            productServiceId: meta.productServiceId,
            productName: meta.productName,
            whoToCheck,
            checklistItems: sorted,
            steps: sorted.length,
            stepCount: sorted.length,
          });
        }
      } catch (err) {
        console.error(
          `Failed to load checklist items for complaint type ${meta.complaintTypeId}:`,
          err
        );
      }
    })
  );

  return [...templateMap.values()].filter((t) => (t.stepCount || 0) > 0);
}

// ---------------------------------------------------------------------------
// Customer Assets / Products (CRM)
// ---------------------------------------------------------------------------

/**
 * Maps a CRM customer-asset list record.
 * Label: asset / product name  |  Value: assetId
 */
export function mapAsset(item) {
  const assetId = item?.assetId ?? item?.id;
  const name =
    item?.assetName ??
    item?.productName ??
    item?.name ??
    (assetId != null ? `Asset #${assetId}` : "Asset");

  return {
    id:      assetId != null ? String(assetId) : "",
    assetId,
    name,
    raw:     item,
  };
}

/**
 * GET /saas/crm/customer-assets/search/customer-location
 *   ?customerId=&locationId=&page=&size=&sortBy=assetId&sortDir=ASC
 * CRM03S
 */
export async function searchCustomerAssets({
  page       = 0,
  size       = 10,
  sortBy     = "assetId",
  sortDir    = "ASC",
  customerId,
  locationId,
} = {}) {
  if (customerId == null || customerId === "" || locationId == null || locationId === "") {
    return [];
  }

  const params = new URLSearchParams({
    customerId: String(customerId),
    locationId: String(locationId),
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await crmGet(
    `${ENDPOINTS.searchAssetsByCustomerLocation}?${params.toString()}`,
    SERVICE_CODES.searchAssets
  );

  return unwrapList(response).map(mapAsset).filter((a) => a.id);
}

/**
 * Maps a CRM customer-asset detail record for Product Information cards.
 * Dates are returned as ISO strings; the UI formats them for display.
 */
export function mapAssetDetail(item) {
  if (!item) {
    return null;
  }

  const warrantyType =
    item?.warrantyType ??
    item?.warrantyPeriod ??
    item?.warrantyStatus ??
    item?.warranty ??
    "";

  return {
    assetId:        item?.assetId ?? item?.id ?? null,
    assetName:      item?.assetName ?? item?.name ?? "",
    assetCategory:  item?.assetCategory ?? item?.category ?? "",
    manufacturer:   item?.manufacturer ?? "",
    modelNumber:    item?.modelNumber ?? item?.model ?? "",
    serialNumber:   item?.serialNumber ?? item?.serial ?? "",
    installDate:    item?.installDate ?? "",
    warrantyType,
    warrantyExpiry: item?.warrantyExpiry ?? item?.warrantyEndDate ?? "",
    amcStartDate:   item?.AMCStartDate ?? item?.amcStartDate ?? item?.amcstartDate ?? "",
    amcEndDate:     item?.AMCEndDate ?? item?.amcEndDate ?? item?.amcendDate ?? "",
    amcType:
      item?.amcType ??
      item?.amctype ??
      item?.AMCType ??
      "",
    amcStatus:
      formatAmcTypeLabel(
        item?.amcType ??
          item?.amctype ??
          item?.AMCType ??
          item?.amcStatus ??
          ""
      ) || deriveAmcStatus(warrantyType),
    assetStatus:    item?.assetStatus ?? item?.status ?? "",
    raw:            item,
  };
}

/**
 * GET /saas/crm/customer-assets/{assetId}
 * CRM03S
 */
export async function fetchCustomerAssetById(assetId) {
  if (assetId == null || assetId === "") return mapAssetDetail(null);

  const response = await crmGet(
    ENDPOINTS.assetById(assetId),
    SERVICE_CODES.assetById
  );

  return mapAssetDetail(unwrapItem(response));
}

/**
 * Maps ticket priority label → API numeric code.
 * Critical=1, High=2, Medium=3, Low=4
 */
function mapPriorityToCode(priority) {
  const map = {
    Critical: 1,
    High: 2,
    Medium: 3,
    Low: 4,
  };
  if (priority == null || priority === "") return 0;
  if (typeof priority === "number" && Number.isFinite(priority)) return priority;
  if (map[priority] != null) return map[priority];
  const n = Number(priority);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Derives amcStatus display string from warrantyType enum values.
 * e.g. NON_WARRANT_AMC_NON_COMPREHENSIVE_SERVICE → "Non-Comprehensive AMC"
 */
export function deriveAmcStatus(warrantyType) {
  const t = String(warrantyType || "").toUpperCase();
  if (!t) return "";
  if (t.includes("NON_COMPREHENSIVE") || t.includes("NON-COMPREHENSIVE")) {
    return "Non-Comprehensive AMC";
  }
  if (t.includes("COMPREHENSIVE")) return "Comprehensive AMC";
  if (t.includes("NO_AMC") || t.includes("WITHOUT_AMC")) return "No AMC";
  return "";
}

/** Prefer explicit amcType fields, then fall back to legacy derived labels. */
export function resolveAmcTypeLabel(source) {
  if (source == null) return "";
  if (typeof source === "string") return formatAmcTypeLabel(source);
  const raw =
    source?.amcType ??
    source?.amctype ??
    source?.AMCType ??
    source?.amcStatus ??
    source?.raw?.amcType ??
    source?.raw?.amctype ??
    source?.raw?.AMCType ??
    source?.raw?.amcStatus ??
    "";
  if (raw) return formatAmcTypeLabel(raw);
  return deriveAmcStatus(source?.warrantyType ?? source?.raw?.warrantyType);
}

/** Display label for AMC values: No_AMC → No AMC, Comprehensive → Comprehensive. */
export function formatAmcTypeLabel(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Canonical AMC key for equality checks.
 * "Comprehensive", "Comprehensive AMC", "COMPREHENSIVE_AMC" → "comprehensive"
 * "No_AMC", "No AMC" → "no"
 * "Non Comprehensive AMC" → "noncomprehensive"
 */
export function normalizeAmcKey(value) {
  let t = String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!t || t === "—") return "";
  t = t.replace(/\bamc\b/g, "").replace(/\s+/g, " ").trim();
  if (!t || t === "no" || t === "none" || t === "without") return "no";
  if (t.includes("non comprehensive") || t.includes("noncomprehensive")) {
    return "noncomprehensive";
  }
  if (t.includes("comprehensive")) return "comprehensive";
  return t.replace(/\s+/g, "");
}

/** True when two AMC labels refer to the same type. */
export function amcTypesMatch(a, b) {
  const ka = normalizeAmcKey(a);
  const kb = normalizeAmcKey(b);
  if (!ka && !kb) return true;
  if (!ka || !kb) return false;
  return ka === kb;
}

/** Display label for call type: Service_Call → Service Call. */
export function formatCallTypeLabel(value) {
  if (value == null || value === "") return "";
  return String(value).replace(/_/g, " ").replace(/-/g, " ").replace(/\s+/g, " ").trim();
}

function resolveManagerId() {
  const emp = sessionDB.getEmpObj() || {};
  const raw =
    emp.manager_id ??
    emp.managerId ??
    emp.reporting_manager_id ??
    emp.reportingManagerId ??
    emp.manager_emp_id ??
    null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Returns a positive numeric ID, or null if missing/invalid (never 0). */
function toPositiveId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** First valid positive ID from candidates. */
function pickPositiveId(...candidates) {
  for (const candidate of candidates) {
    const id = toPositiveId(candidate);
    if (id != null) return id;
  }
  return null;
}

/**
 * Ensures update payload FK fields are valid (> 0). Throws with a clear message.
 */
export function assertValidUpdateComplaintPayload(payload) {
  const required = [
    "customerId",
    "productServiceId",
    "complaintTypeId",
    "managerId",
  ];
  const invalid = required.filter((key) => {
    const id = toPositiveId(payload?.[key]);
    return id == null;
  });

  // Optional FKs: if present they must not be 0
  const optional = ["assetId", "locationId", "technicianId", "createdBy", "updatedBy"];
  for (const key of optional) {
    if (payload?.[key] == null || payload[key] === "") continue;
    if (toPositiveId(payload[key]) == null) {
      invalid.push(key);
    }
  }

  if (invalid.length > 0) {
    throw new Error(
      `Invalid update payload: foreign key field(s) missing or 0 — ${invalid.join(", ")}. ` +
        "Preserve existing complaint IDs; do not default them to 0."
    );
  }
}

/**
 * Builds PUT /customer-complaints/update/{id} body from review ticket + options.
 * Preserves existing FK IDs from the loaded complaint; never defaults them to 0.
 */
export function buildUpdateComplaintPayload(
  ticket,
  {
    technicianId,
    technicianRemarks,
    complaintStatus,
    complaintChecklist,
    createdBy,
    managerId,
    scheduledDate,
    scheduledVisits,
    closureMode,
    customerClosureRemarks,
    customerRating,
    managerClosureRemarks,
    managerRating,
  } = {}
) {
  const raw = ticket?.raw && typeof ticket.raw === "object" ? ticket.raw : {};
  const empId = toPositiveId(sessionDB.getEmpId());

  const priorityRaw = raw.priority ?? ticket?.priority;
  const priorityValue =
    typeof priorityRaw === "number" && Number.isFinite(priorityRaw)
      ? priorityRaw
      : mapPriorityToCode(priorityRaw);

  const status =
    complaintStatus != null && complaintStatus !== ""
      ? String(complaintStatus)
      : raw.complaintStatus ?? ticket?.statusRaw ?? ticket?.status ?? "Assigned";

  // Prefer existing complaint FKs; only then fall back to session / explicit overrides
  const customerId = pickPositiveId(ticket?.customerId, raw.customerId);
  const productServiceId = pickPositiveId(
    ticket?.productServiceId,
    raw.productServiceId
  );
  const complaintTypeId = pickPositiveId(
    ticket?.complaintTypeId,
    raw.complaintTypeId
  );
  const assetId = pickPositiveId(ticket?.assetId, raw.assetId);
  const locationId = pickPositiveId(ticket?.locationId, raw.locationId);
  const resolvedManagerId = pickPositiveId(
    managerId,
    ticket?.managerId,
    raw.managerId,
    resolveManagerId(),
    empId
  );
  const resolvedTechnicianId = pickPositiveId(
    technicianId,
    ticket?.technicianId,
    raw.technicianId
  );
  const resolvedCreatedBy = pickPositiveId(createdBy, raw.createdBy, empId);
  const complaintId = pickPositiveId(
    ticket?.complaintId,
    ticket?.id,
    raw.complaintId
  );

  const resolveOptionalText = (override, ...fallbacks) => {
    if (override !== undefined && override !== null) return String(override);
    for (const value of fallbacks) {
      if (value != null && value !== "") return String(value);
    }
    return "";
  };

  const resolveOptionalRanking = (override, ...fallbacks) => {
    if (override !== undefined && override !== null && override !== "") {
      const n = Number(override);
      return Number.isFinite(n) ? n : null;
    }
    for (const value of fallbacks) {
      if (value == null || value === "") continue;
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const payload = {
    customerId,
    productServiceId,
    complaintTypeId,
    warrantyType: ticket?.warrantyType ?? raw.warrantyType ?? "",
    priority: priorityValue,
    complaintMode: ticket?.complaintMode ?? raw.complaintMode ?? "",
    scheduledDate:
      toVisitDateParam(
        scheduledDate ?? ticket?.scheduledDate ?? ticket?.visitDate ?? raw.scheduledDate
      ) || "",
    complaintRecievedOn:
      toVisitDateParam(
        ticket?.complaintRecievedOn ??
          raw.complaintRecievedOn ??
          ticket?.complaintDate ??
          raw.complaintDate
      ) || "",
    amcStatus:
      ticket?.amcStatus ??
      raw.amcStatus ??
      deriveAmcStatus(ticket?.warrantyType ?? raw.warrantyType),
    resolvedOn: raw.resolvedOn ?? "",
    createdBy: resolvedCreatedBy,
    contactNumber: ticket?.contactNumber ?? raw.contactNumber ?? "",
    pointOfContact: ticket?.pointOfContact ?? raw.pointOfContact ?? "",
    email: ticket?.email ?? raw.email ?? "",
    managerId: resolvedManagerId,
    updatedBy: empId,
    complaintDescription: ticket?.description ?? raw.complaintDescription ?? "",
    technicianRemarks: resolveOptionalText(
      technicianRemarks,
      ticket?.technicianRemarks,
      raw.technicianRemarks,
      ticket?.techRemarks,
      raw.techRemarks,
      raw.remarks
    ),
    closureMode: resolveOptionalText(
      closureMode,
      ticket?.closureMode,
      raw.closureMode
    ),
    customerClosureRemarks: resolveOptionalText(
      customerClosureRemarks,
      ticket?.customerClosureRemarks,
      raw.customerClosureRemarks,
      ticket?.customerServiceRemarks,
      raw.customerServiceRemarks
    ),
    managerClosureRemarks: resolveOptionalText(
      managerClosureRemarks,
      ticket?.managerClosureRemarks,
      raw.managerClosureRemarks,
      ticket?.managerRemarks,
      raw.managerRemarks
    ),
    complaintStatus: status,
  };

  const resolvedCustomerRating = resolveOptionalRanking(
    customerRating,
    ticket?.customerRating,
    raw.customerRating,
    ticket?.customerRanking,
    raw.customerRanking,
    ticket?.customerRatings,
    raw.customerRatings
  );
  if (resolvedCustomerRating != null) {
    payload.customerRating = resolvedCustomerRating;
  }

  const resolvedManagerRating = resolveOptionalRanking(
    managerRating,
    ticket?.managerRating,
    raw.managerRating,
    ticket?.managerRanking,
    raw.managerRanking,
    ticket?.managerRatings,
    raw.managerRatings
  );
  if (resolvedManagerRating != null) {
    payload.managerRating = resolvedManagerRating;
  }

  // Only include optional FKs when they are valid (> 0) — never send 0
  if (assetId != null) payload.assetId = assetId;
  if (locationId != null) payload.locationId = locationId;
  if (resolvedTechnicianId != null) payload.technicianId = resolvedTechnicianId;

  if (Array.isArray(complaintChecklist)) {
    payload.complaintChecklist = complaintChecklist;
  }

  if (Array.isArray(scheduledVisits)) {
    payload.scheduledVisits = scheduledVisits
      .map((visit) => {
        const visitDate =
          toVisitDateParam(visit?.visitDate ?? visit?.date ?? visit) || "";
        if (!visitDate) return null;
        const entry = {
          complaintId: pickPositiveId(visit?.complaintId, complaintId) ?? complaintId,
          visitDate,
          scheduledBy:
            pickPositiveId(visit?.scheduledBy, resolvedManagerId, empId) ??
            resolvedManagerId ??
            empId,
        };
        const visitId = Number(visit?.visitId);
        if (Number.isFinite(visitId) && visitId > 0) {
          entry.visitId = visitId;
        }
        return entry;
      })
      .filter(Boolean);
  }

  return payload;
}

/**
 * PUT /saas/fsm/customer-complaints/update/{complaintId}
 * FSM03U
 */
export async function updateCustomerComplaint(complaintId, payload) {
  if (complaintId == null || complaintId === "") {
    throw new Error("Complaint ID is required.");
  }

  assertValidUpdateComplaintPayload(payload);

  // Inspectable in the browser Network tab / console
  console.log("[FSM] updateCustomerComplaint request", {
    complaintId,
    path: ENDPOINTS.updateComplaint(complaintId),
    payload,
  });

  const response = await fsmPut(
    ENDPOINTS.updateComplaint(complaintId),
    payload,
    SERVICE_CODES.updateComplaint
  );

  return unwrapItem(response) ?? response;
}

/**
 * Builds the create-complaint payload from Create Ticket form state.
 * Shape matches POST /saas/fsm/customer-complaints/add.
 */
export function buildCreateComplaintPayload({
  customer,
  system,
  complaint,
  product,
  location,
  warrantyType,
  amcType,
  callType,
  priority,
  pointOfContact,
  addedNewCrmPoc = 'N',
  contactNumber,
  email,
  complaintMode,
  complaintRecievedOn,
  complaintDescription,
  managerId,
}) {
  const toId = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const resolvedWarrantyType = warrantyType ?? "";
  const resolvedAmcType = amcType ?? "";
  const createdBy = toId(sessionDB.getEmpId());
  const resolvedManagerId = pickPositiveId(
    managerId,
    resolveManagerId(),
    createdBy
  );
  const actorRole = sessionDB.getCanonicalRoleName() || "";

  return {
    customerId:           toId(customer?.customerId ?? customer?.id),
    customerName:
      customer?.name ??
      customer?.customerName ??
      customer?.companyName ??
      "",
    productServiceId:     toId(system?.productServiceId ?? system?.id),
    complaintTypeId:      toId(complaint?.complaintTypeId ?? complaint?.id),
    assetId:              toId(product?.assetId ?? product?.id) || null,
    locationId:           toId(location?.locationId ?? location?.id),
    warrantyType:         resolvedWarrantyType,
    amcType:              resolvedAmcType,
    callType:             callType ?? "",
    priority:             mapPriorityToCode(priority),
    complaintMode:        complaintMode ?? "",
    complaintRecievedOn:
      toVisitDateParam(complaintRecievedOn) || "",
    amcStatus:            resolvedAmcType || deriveAmcStatus(resolvedWarrantyType),
    resolvedOn:           "",
    createdBy,
    contactNumber:        contactNumber ?? "",
    pointOfContact:       pointOfContact ?? "",
    addedNewCrmPoc:        addedNewCrmPoc === "Y" ? "Y" : "N",
    email:                email ?? "",
    managerId:            resolvedManagerId,
    complaintDescription: complaintDescription ?? "",
    actorRole,
    complaintStatus:      "Under_Review",
  };
}

/**
 * POST /saas/fsm/customer-complaints/add
 * FSM03A
 */
export async function createCustomerComplaint(payload) {
  const response = await fsmPost(
    ENDPOINTS.addComplaint,
    payload,
    SERVICE_CODES.addComplaint
  );
  return unwrapItem(response) ?? response;
}

/**
 * Maps an HRMS warranty_types row into a dropdown option.
 * Value sent on create is the warranty type code/name string.
 */
export function mapWarrantyType(item) {
  if (item == null) return null;
  if (typeof item === "string") {
    const value = item.trim();
    if (!value) return null;
    return {
      id: value,
      label: value.replace(/_/g, " "),
      value,
    };
  }

  const value =
    item?.warrantyType ??
    item?.warrantyTypeCode ??
    item?.code ??
    item?.name ??
    item?.warrantyTypeName ??
    "";
  const id =
    item?.warrantyTypeId ??
    item?.id ??
    value;
  const label =
    item?.warrantyTypeName ??
    item?.name ??
    item?.label ??
    (value ? String(value).replace(/_/g, " ") : "");

  if (!id && !value) return null;

  return {
    id: String(id ?? value),
    label: label || String(value),
    value: String(value || id),
  };
}

/**
 * GET /saas/hrms/org/warranty_types?tenantId={session}
 * HR14A (org lookup — same family as other /org/* endpoints)
 */
export async function searchWarrantyTypes() {
  const tenantId = sessionDB.getTenantId();
  if (tenantId === "" || tenantId == null) {
    throw new Error("Tenant ID is missing from the session.");
  }

  const response = await hrmsGet(
    `/org/warranty_types?tenantId=${encodeURIComponent(tenantId)}`,
    "HR14A"
  );

  return unwrapList(response).map(mapWarrantyType).filter(Boolean);
}

const PRIORITY_CODE_TO_LABEL = {
  1: "Critical",
  2: "High",
  3: "Medium",
  4: "Low",
};

function mapPriorityCodeToLabel(priority) {
  if (priority == null || priority === "") return "—";
  if (typeof priority === "string" && Number.isNaN(Number(priority))) {
    return priority;
  }
  const code = Number(priority);
  return PRIORITY_CODE_TO_LABEL[code] ?? String(priority);
}

/** Normalizes API complaint status codes into display labels. */
export function mapComplaintStatusLabel(status) {
  if (status == null || status === "") return "Pending Review";
  const raw = String(status).trim();
  const key = raw.toLowerCase().replace(/[\s-]+/g, "_");
  const map = {
    pending_review: "Under Review",
    under_review: "Under Review",
    on_hold: "On Hold",
    hold: "On Hold",
    rejected: "Rejected",
    reject: "Rejected",
    proceed: "Assigned",
    assigned: "Assigned",
    accepted: "Accepted",
    scheduled: "Scheduled",
    in_progress: "In Progress",
    quotation_submitted: "Quotation Submitted",
    quotation_accepted: "Quotation Accepted",
    closure_requested: "Closure Requested",
    closure_request: "Closure Requested",
    closure_verified: "Closure Verified",
    closed: "Closed",
  };
  return map[key] ?? raw;
}

/** Maps manager-status newStatus query values to UI status labels. */
export function mapManagerActionToStatus(newStatus) {
  const key = String(newStatus || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (key === "proceed") return "Assigned";
  if (key === "on_hold" || key === "hold") return "On Hold";
  if (key === "rejected" || key === "reject") return "Rejected";
  return mapComplaintStatusLabel(newStatus);
}

/** Allowed manager-status API values. */
export const MANAGER_STATUS_ACTIONS = {
  proceed: "Proceed",
  hold: "On_Hold",
  reject: "Rejected",
};

/**
 * PUT /saas/fsm/customer-complaints/manager-status/{complaintId}?managerId=&newStatus=
 * newStatus: Proceed | On_Hold | Rejected
 * FSM03U
 *
 * managerId defaults to the signed-in employee (acting reviewer).
 */
export async function updateManagerComplaintStatus(
  complaintId,
  newStatus,
  managerId
) {
  if (complaintId == null || complaintId === "") {
    throw new Error("Complaint ID is required.");
  }

  const allowed = new Set(["Proceed", "On_Hold", "Rejected"]);
  const action = String(newStatus || "").trim();
  if (!allowed.has(action)) {
    throw new Error("Invalid manager status. Allowed: On_Hold, Rejected, Proceed");
  }

  const resolvedManagerId = Number(
    managerId != null && managerId !== ""
      ? managerId
      : sessionDB.getEmpId()
  );
  if (!Number.isFinite(resolvedManagerId) || resolvedManagerId <= 0) {
    throw new Error("Manager ID is missing from the session.");
  }

  const params = new URLSearchParams({
    managerId: String(resolvedManagerId),
    newStatus: action,
  });

  const response = await fsmPut(
    `${ENDPOINTS.managerStatus(complaintId)}?${params.toString()}`,
    undefined,
    SERVICE_CODES.managerStatus
  );

  return unwrapItem(response) ?? response;
}

/**
 * Maps HRMS current-employment row into a technician dropdown option.
 */
export function mapTechnician(item) {
  const empId = item?.empId ?? item?.employeeId ?? item?.emp_Id ?? item?.id;
  const first = item?.firstName ?? item?.first_name ?? "";
  const last = item?.lastName ?? item?.last_name ?? "";
  const fullName = `${first} ${last}`.trim();
  const name =
    item?.name ??
    item?.employeeName ??
    (fullName || (empId != null ? `Employee #${empId}` : "Technician"));
  const contact =
    item?.mobile ??
    item?.phone ??
    item?.contactNumber ??
    item?.contact ??
    item?.officialMobile ??
    item?.personalMobile ??
    item?.mobileNumber ??
    "";

  return {
    id: empId != null ? String(empId) : "",
    empId: empId != null ? Number(empId) : 0,
    name,
    contact: contact ? String(contact) : "",
    raw: item,
  };
}

/**
 * GET /saas/hrms/current-employment/search/canonicalRoleName?searchTerm=technician
 * HR02S
 */
export async function searchTechnicians({
  page = 0,
  size = 10,
  sortBy = "first_name",
  sortDir = "ASC",
  searchTerm = "technician",
} = {}) {
  const params = new URLSearchParams({
    searchTerm,
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await hrmsGet(
    `/current-employment/search/canonicalRoleName?${params.toString()}`,
    SERVICE_CODES.searchTechnicians
  );

  return unwrapList(response).map(mapTechnician).filter((t) => t.id);
}

/**
 * True when the technician has accepted the assignment.
 * After accept, complaint status is Accepted; manager then schedules (Scheduled).
 */
export function hasTechnicianAccepted(ticket) {
  if (!ticket) return false;
  if (ticket.isAccepted === true) return true;
  const raw = ticket.raw && typeof ticket.raw === "object" ? ticket.raw : {};
  if (raw.isAccepted === true || raw.technicianAccepted === true) return true;
  const key = String(ticket.statusRaw || ticket.status || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return [
    "accepted",
    "scheduled",
    "in_progress",
    "quotation_submitted",
    "quotation_accepted",
    "closure_requested",
    "closure_verified",
    "closed",
  ].includes(key);
}

/** Normalized status key: pending_review, assigned, accepted, scheduled, … */
export function getComplaintStatusKey(ticket) {
  if (!ticket) return "";
  const raw =
    ticket.statusRaw ??
    ticket.raw?.complaintStatus ??
    ticket.raw?.status ??
    ticket.status ??
    "";
  return String(raw)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

/**
 * PUT /saas/fsm/customer-complaints/technician-status/{complaintId}?technicianId=&isAccepted=
 * FSM03U
 */
export async function updateTechnicianComplaintStatus(
  complaintId,
  isAccepted,
  technicianId
) {
  if (complaintId == null || complaintId === "") {
    throw new Error("Complaint ID is required.");
  }

  const resolvedTechId = Number(
    technicianId != null && technicianId !== ""
      ? technicianId
      : sessionDB.getEmpId()
  );
  if (!Number.isFinite(resolvedTechId) || resolvedTechId <= 0) {
    throw new Error("Technician ID is missing from the session.");
  }

  const params = new URLSearchParams({
    technicianId: String(resolvedTechId),
    isAccepted: String(Boolean(isAccepted)),
  });

  const response = await fsmPut(
    `${ENDPOINTS.technicianStatus(complaintId)}?${params.toString()}`,
    undefined,
    SERVICE_CODES.technicianStatus
  );

  return unwrapItem(response) ?? response;
}

/**
 * Converts date / datetime input → LocalDateTime for schedule API.
 * Expected: 2026-07-23T14:30:00
 */
export function toVisitDateParam(value) {
  if (!value) return "";
  const raw = String(value).trim();

  // Already LocalDateTime-like
  const fullMatch = raw.match(
    /^(\d{4}-\d{2}-\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/
  );
  if (fullMatch) {
    const [, datePart, hh, mm, ss] = fullMatch;
    return `${datePart}T${hh}:${mm}:${ss || "00"}`;
  }

  // YYYY-MM-DD only → midnight
  const dateOnly = raw.match(/^(\d{4}-\d{2}-\d{2})$/);
  if (dateOnly) {
    return `${dateOnly[1]}T00:00:00`;
  }

  // DD-MM-YYYY (legacy) → midnight
  const dmy = raw.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2]}-${dmy[1]}T00:00:00`;
  }

  return raw;
}

/**
 * PUT /saas/fsm/customer-complaints/schedule/{complaintId}?managerId=&visitDate=2026-07-23T14:30:00
 * FSM03U
 */
export async function scheduleComplaintVisit(complaintId, visitDate, managerId) {
  if (complaintId == null || complaintId === "") {
    throw new Error("Complaint ID is required.");
  }
  const visitDateParam = toVisitDateParam(visitDate);
  if (!visitDateParam) {
    throw new Error("Visit date is required.");
  }

  const resolvedManagerId = Number(
    managerId != null && managerId !== ""
      ? managerId
      : sessionDB.getEmpId()
  );
  if (!Number.isFinite(resolvedManagerId) || resolvedManagerId <= 0) {
    throw new Error("Manager ID is missing from the session.");
  }

  const params = new URLSearchParams({
    managerId: String(resolvedManagerId),
    visitDate: visitDateParam,
  });

  const response = await fsmPut(
    `${ENDPOINTS.scheduleVisit(complaintId)}?${params.toString()}`,
    undefined,
    SERVICE_CODES.scheduleVisit
  );

  return unwrapItem(response) ?? response;
}

function toDateOnly(value) {
  if (!value) return "";
  const str = String(value);
  if (str.includes("T")) return str.slice(0, 10);
  return str.slice(0, 10);
}

/**
 * Prefer scheduledVisits[].visitDate (full datetime) over date-only scheduledDate.
 * Returns the earliest visit by visitDate when multiple exist.
 */
function pickPrimaryVisitDateTime(item) {
  const visits = Array.isArray(item?.scheduledVisits) ? item.scheduledVisits : [];
  const fromVisits = visits
    .map((v) => v?.visitDate)
    .filter(Boolean)
    .sort((a, b) => {
      const ta = Date.parse(String(a).replace(" ", "T")) || 0;
      const tb = Date.parse(String(b).replace(" ", "T")) || 0;
      return ta - tb;
    });
  if (fromVisits.length > 0) return fromVisits[0];

  return (
    item?.visitDate ??
    item?.firstVisitDate ??
    item?.scheduledVisitDate ??
    item?.scheduledDate ??
    item?.visits?.[0]?.visitDate ??
    item?.complaintVisits?.[0]?.visitDate ??
    item?.fsmComplaintVisits?.[0]?.visitDate ??
    ""
  );
}

function resolveComplaintNumber(item, complaintId) {
  const raw = item?.complaintNumber ?? item?.complaintNo;
  const text = raw != null && String(raw).trim() !== "" ? String(raw).trim() : "";
  const withoutPrefix = text.replace(/^CMP-?/i, "");
  if (withoutPrefix) return withoutPrefix;
  if (complaintId != null && complaintId !== "") return String(complaintId);
  return "—";
}

/**
 * Maps a customer-complaint search record into the tickets-list row shape.
 */
export function mapComplaintTicket(item) {
  const complaintId = item?.complaintId ?? item?.id;
  const customerId =
    item?.customerId ??
    item?.customer?.customerId ??
    null;
  const productServiceId =
    item?.productServiceId ??
    item?.productService?.productServiceId ??
    null;

  const customerName =
    item?.companyName ??
    item?.customerName ??
    item?.customer?.companyName ??
    item?.customer?.customerName ??
    "";

  const productServiceName =
    item?.productServiceName ??
    item?.productService?.productServiceName ??
    item?.productService?.name ??
    item?.systemName ??
    "";

  const locationName =
    item?.locationName ??
    item?.location?.locationName ??
    "";

  const assetName =
    item?.assetName ??
    item?.productName ??
    item?.asset?.assetName ??
    "";

  const complaintTypeName =
    item?.complaintTypeName ??
    item?.complaintType ??
    item?.typeOfComplaint ??
    item?.complaint?.complaintTypeName ??
    "";

  const statusRaw = item?.complaintStatus ?? item?.status ?? "";

  let status = mapComplaintStatusLabel(statusRaw);
  // Keep API status as-is. Lifecycle:
  //   Manager assigns technician → Assigned
  //   Technician accept → Accepted
  //   Manager schedules visit → Scheduled
  //   Checklist work → In_Progress
  //   Quotation raised → Quotation_Submitted
  //   Quotation accepted → Quotation_Accepted
  //   Tech requests closure → Closure_Requested

  return {
    id: complaintId != null ? String(complaintId) : "",
    complaintId,
    complaintNo: resolveComplaintNumber(item, complaintId),
    customerName: customerName || "—",
    customerSub: locationName || "",
    customerId,
    system: productServiceName || "—",
    productServiceId,
    productServiceName: productServiceName || "",
    locationName: locationName || "",
    assetName: assetName || "",
    complaintTypeName: complaintTypeName || "",
    priority: mapPriorityCodeToLabel(item?.priority),
    status,
    statusRaw,
    createdAt: toDateOnly(item?.createdOn ?? item?.createdAt ?? item?.scheduledDate),
    raw: item,
  };
}

function mapQuotationLineItem(item) {
  if (!item || typeof item !== "object") return null;
  const quantity = item.quantity ?? item.qty ?? null;
  const unitPrice = coerceQuotationAmount(
    item.unitPrice ??
      item.sellingSupplyRate ??
      item.sellingRate ??
      item.rate ??
      item.price ??
      item.netPrice ??
      item.netPriceRulePct ??
      null
  );
  const amount = coerceQuotationAmount(
    item.amount ??
      item.sellingSupplyAmount ??
      item.lineTotal ??
      item.total ??
      item.sellingAmount ??
      item.sellingPrice ??
      null
  );
  return {
    id: item.lineItemId ?? item.id ?? null,
    slNo: item.slNo ?? item.sl_no ?? null,
    description:
      item.description ??
      item.itemDescription ??
      item.productName ??
      item.systemName ??
      item.name ??
      "",
    brandId: item.brandId ?? item.brand ?? item.make ?? "",
    productId: item.productId ?? item.partNo ?? "",
    hsnCode: item.hsnCode ?? item.hsn ?? "",
    uom: item.uom ?? item.unit ?? "",
    systemName: item.systemName ?? "",
    quantity,
    listPrice: coerceQuotationAmount(item.listPrice),
    netPriceRulePct: coerceQuotationAmount(item.netPriceRulePct),
    discountPct: coerceQuotationAmount(item.discountPct),
    inputSupplyRate: coerceQuotationAmount(item.inputSupplyRate),
    inputSupplyAmount: coerceQuotationAmount(item.inputSupplyAmount),
    sellingSupplyRate: coerceQuotationAmount(item.sellingSupplyRate),
    sellingSupplyAmount: coerceQuotationAmount(item.sellingSupplyAmount),
    inputInstallationRate: coerceQuotationAmount(item.inputInstallationRate),
    inputInstallationAmount: coerceQuotationAmount(item.inputInstallationAmount),
    sellingInstallationRate: coerceQuotationAmount(item.sellingInstallationRate),
    sellingInstallationAmount: coerceQuotationAmount(item.sellingInstallationAmount),
    multiplier: coerceQuotationAmount(item.multiplier),
    remarks: item.remarks ?? "",
    unitPrice,
    amount,
    raw: item,
  };
}

function coerceQuotationAmount(value) {
  if (value == null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

function mapQuotationPmc(item) {
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id ?? item.pmcConfigId ?? null,
    category: item.category ?? "",
    headName: item.headName ?? item.name ?? "",
    costHeadType: item.costHeadType ?? "",
    nos: item.nos ?? null,
    months: coerceQuotationAmount(item.months),
    rate: coerceQuotationAmount(item.rate),
    totalCost: coerceQuotationAmount(item.totalCost),
    raw: item,
  };
}

function mapQuotationTerm(item) {
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id ?? null,
    title: item.title ?? "",
    description: item.description ?? "",
    displayOrder: item.displayOrder ?? null,
    raw: item,
  };
}

function mapSystemSummary(item) {
  if (!item || typeof item !== "object") return null;
  return {
    systemName: item.systemName ?? "",
    totalInputSupplyAmount: coerceQuotationAmount(item.totalInputSupplyAmount),
    totalInputInstallationAmount: coerceQuotationAmount(item.totalInputInstallationAmount),
    totalSellingSupplyAmount: coerceQuotationAmount(item.totalSellingSupplyAmount),
    totalSellingInstallationAmount: coerceQuotationAmount(item.totalSellingInstallationAmount),
    totalCostPrice: coerceQuotationAmount(item.totalCostPrice),
    totalSellingPrice: coerceQuotationAmount(item.totalSellingPrice),
    grossProfit: coerceQuotationAmount(item.grossProfit),
    grossMarginPct: coerceQuotationAmount(item.grossMarginPct),
    raw: item,
  };
}

/** Normalize a quotation from complaint detail or CRM forComplaintId. */
export function mapQuotation(item) {
  if (!item || typeof item !== "object") return null;

  const quotationId =
    item.quotationId ?? item.id ?? item.quoteId ?? item.quotation_id ?? null;
  const lineSource = Array.isArray(item.lineItems)
    ? item.lineItems
    : Array.isArray(item.lines)
      ? item.lines
      : Array.isArray(item.items)
        ? item.items
        : Array.isArray(item.quotationItems)
          ? item.quotationItems
          : [];

  const finalSellingPrice = coerceQuotationAmount(
    item.finalSellingPrice ??
      item.totalAmount ??
      item.grandTotal ??
      item.amount ??
      item.netAmount ??
      item.sellingPrice ??
      item.customerSellingTotal ??
      item.totalSellingPrice
  );

  return {
    id: quotationId != null ? String(quotationId) : item.refId ? String(item.refId) : "",
    quotationId,
    quotationNumber:
      item.quotationNumber ??
      item.quoteNumber ??
      item.quotationNo ??
      item.quotationCode ??
      item.refId ??
      item.referenceId ??
      (quotationId != null ? `Q-${quotationId}` : ""),
    versionNo: item.versionNo ?? item.version ?? null,
    status: item.status ?? item.quotationStatus ?? "",
    type: item.type ?? item.quotationType ?? "",
    totalAmount: finalSellingPrice,
    finalSellingPrice,
    currency: item.currency ?? item.currencyCode ?? "",
    createdOn: item.createdOn ?? item.createdAt ?? item.quotationDate ?? "",
    updatedOn: item.updatedOn ?? item.updatedAt ?? "",
    validUntil: item.validUntil ?? item.validTill ?? item.expiryDate ?? "",
    customerId: item.customerId ?? null,
    customerName:
      item.customerName ??
      item.companyName ??
      item.customer?.companyName ??
      item.customer?.customerName ??
      "",
    locationId: item.locationId ?? null,
    locationName: item.locationName ?? item.address ?? "",
    contactName: item.contactName ?? item.pointOfContactName ?? "",
    contactPhone: item.contactPhone ?? item.phone ?? "",
    contactEmail: item.contactEmail ?? item.email ?? "",
    createdByName: item.createdByName ?? "",
    managerNote: item.managerNote ?? "",
    remarks: item.remarks ?? item.notes ?? item.description ?? item.managerNote ?? "",
    overheadsPct: coerceQuotationAmount(item.overheadsPct),
    markUpPct: coerceQuotationAmount(item.markUpPct),
    totalInputSupplyAmount: coerceQuotationAmount(item.totalInputSupplyAmount),
    totalInputInstallationAmount: coerceQuotationAmount(item.totalInputInstallationAmount),
    totalSellingSupplyAmount: coerceQuotationAmount(item.totalSellingSupplyAmount),
    totalSellingInstallationAmount: coerceQuotationAmount(item.totalSellingInstallationAmount),
    totalPmcCost: coerceQuotationAmount(item.totalPmcCost),
    totalInternalCost: coerceQuotationAmount(item.totalInternalCost),
    grossProfit: coerceQuotationAmount(item.grossProfit),
    grossMarginPct: coerceQuotationAmount(item.grossMarginPct),
    netProfit: coerceQuotationAmount(item.netProfit),
    netMarginPct: coerceQuotationAmount(item.netMarginPct),
    requiredApprovalLevel: item.requiredApprovalLevel ?? "",
    approverRoleName: item.approverRoleName ?? "",
    complaintId: item.complaintId ?? item.customerComplaintId ?? null,
    lineItems: lineSource.map(mapQuotationLineItem).filter(Boolean),
    systemSummaries: (Array.isArray(item.systemSummaries) ? item.systemSummaries : [])
      .map(mapSystemSummary)
      .filter(Boolean),
    pmcs: (Array.isArray(item.pmcs) ? item.pmcs : []).map(mapQuotationPmc).filter(Boolean),
    specificTerms: (Array.isArray(item.specificTerms) ? item.specificTerms : [])
      .map(mapQuotationTerm)
      .filter(Boolean),
    raw: item,
  };
}

export function mapQuotations(list) {
  if (!Array.isArray(list)) return [];
  return list.map(mapQuotation).filter((q) => q && (q.id || q.quotationNumber));
}

/**
 * Maps a customer-complaint record into the ticket detail / review shape.
 */
export function mapComplaintDetail(item) {
  const base = mapComplaintTicket(item);
  const complaintTypeName =
    item?.complaintTypeName ??
    item?.complaintType ??
    item?.typeOfComplaint ??
    item?.complaint?.complaintTypeName ??
    base.complaintTypeName ??
    "—";

  const productServiceName =
    item?.productServiceName ??
    item?.productService?.productServiceName ??
    item?.productService?.name ??
    item?.systemName ??
    base.productServiceName ??
    "";

  const rawKey = String(item?.complaintStatus ?? item?.status ?? "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const acceptedFlag =
    item?.isAccepted === true || item?.technicianAccepted === true;
  // Technician has accepted the assignment (status moves to Accepted after accept)
  const isAccepted =
    acceptedFlag ||
    rawKey === "accepted" ||
    rawKey === "scheduled" ||
    rawKey === "in_progress" ||
    rawKey === "quotation_submitted" ||
    rawKey === "quotation_accepted" ||
    rawKey === "closure_requested" ||
    rawKey === "closure_verified" ||
    rawKey === "closed";

  // Preserve the real lifecycle status for display
  const status = base.status;
  const statusRaw = base.statusRaw;

  const technicianName =
    item?.technicianName ??
    item?.technician?.name ??
    item?.technician?.employeeName ??
    [
      item?.technician?.firstName ?? item?.technician?.first_name,
      item?.technician?.lastName ?? item?.technician?.last_name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  const locationName =
    item?.locationName ??
    item?.address ??
    item?.customerLocation ??
    item?.location?.locationName ??
    item?.location?.address ??
    base.locationName ??
    "";

  const assetName =
    item?.assetName ??
    item?.productName ??
    item?.asset?.assetName ??
    base.assetName ??
    "";

  return {
    ...base,
    status,
    statusRaw,
    customerName:
      item?.companyName ??
      item?.customerName ??
      item?.customer?.companyName ??
      item?.customer?.customerName ??
      base.customerName ??
      "—",
    system: productServiceName || "—",
    productServiceName: productServiceName || "",
    locationId: item?.locationId ?? item?.location?.locationId ?? null,
    locationName,
    pointOfContact: item?.pointOfContact ?? item?.contactName ?? "",
    contactNumber: item?.contactNumber ?? item?.phone ?? item?.mobile ?? "",
    email: item?.email ?? "",
    complaintMode: item?.complaintMode ?? "",
    complaintDate:
      item?.complaintRecievedOn ??
      item?.complaintDate ??
      item?.createdOn ??
      item?.createdAt ??
      "",
    complaintRecievedOn:
      item?.complaintRecievedOn ??
      item?.complaintDate ??
      "",
    complaintType: complaintTypeName || "—",
    complaintTypeId: item?.complaintTypeId ?? item?.complaint?.complaintTypeId ?? null,
    description:
      item?.complaintDescription ??
      item?.description ??
      "",
    technicianRemarks:
      item?.technicianRemarks ?? item?.techRemarks ?? item?.remarks ?? "",
    techRemarks:
      item?.technicianRemarks ?? item?.techRemarks ?? item?.remarks ?? "",
    remarks:
      item?.technicianRemarks ?? item?.techRemarks ?? item?.remarks ?? "",
    closureMode: item?.closureMode ?? "",
    customerClosureRemarks:
      item?.customerClosureRemarks ?? item?.customerServiceRemarks ?? "",
    customerServiceRemarks:
      item?.customerClosureRemarks ?? item?.customerServiceRemarks ?? "",
    customerRating: (() => {
      const value =
        item?.customerRating ?? item?.customerRanking ?? item?.customerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    customerRanking: (() => {
      const value =
        item?.customerRating ?? item?.customerRanking ?? item?.customerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    customerRatings: (() => {
      const value =
        item?.customerRating ?? item?.customerRanking ?? item?.customerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    managerClosureRemarks:
      item?.managerClosureRemarks ?? item?.managerRemarks ?? "",
    managerRemarks: item?.managerClosureRemarks ?? item?.managerRemarks ?? "",
    managerRating: (() => {
      const value =
        item?.managerRating ?? item?.managerRanking ?? item?.managerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    managerRanking: (() => {
      const value =
        item?.managerRating ?? item?.managerRanking ?? item?.managerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    managerRatings: (() => {
      const value =
        item?.managerRating ?? item?.managerRanking ?? item?.managerRatings;
      return value != null && value !== "" ? Number(value) : null;
    })(),
    warrantyType: item?.warrantyType ?? "",
    amcType: item?.amcType ?? item?.amctype ?? "",
    amcStatus:
      formatAmcTypeLabel(item?.amcType ?? item?.amctype ?? item?.amcStatus ?? "") ||
      deriveAmcStatus(item?.warrantyType),
    callType: item?.callType ?? "",
    assetId: item?.assetId ?? item?.asset?.assetId ?? null,
    assetName,
    managerId: item?.managerId ?? item?.manager?.empId ?? item?.manager?.managerId ?? null,
    createdBy: item?.createdBy ?? null,
    updatedBy: item?.updatedBy ?? null,
    technicianId:
      item?.technicianId ??
      item?.technician?.empId ??
      item?.technician?.technicianId ??
      null,
    technicianName: technicianName || "",
    // Prefer scheduledVisits visitDate (includes time) over date-only scheduledDate
    visitDate: pickPrimaryVisitDateTime(item),
    scheduledDate: pickPrimaryVisitDateTime(item),
    scheduledVisits: Array.isArray(item?.scheduledVisits)
      ? item.scheduledVisits
      : [],
    isAccepted,
    checklistItems: Array.isArray(item?.checklistItems)
      ? item.checklistItems
      : Array.isArray(item?.complaintChecklist)
        ? item.complaintChecklist
        : [],
    quotations: mapQuotations(
      Array.isArray(item?.quotations)
        ? item.quotations
        : Array.isArray(item?.quotationList)
          ? item.quotationList
          : []
    ),
  };
}

/**
 * GET /saas/fsm/customer-complaints/customer/{customerId}
 * FSM03S
 */
export async function searchComplaintsByCustomer(
  customerId,
  { page = 0, size = 10, sortBy = "complaintId", sortDir = "DESC" } = {}
) {
  if (customerId == null || customerId === "") {
    return { items: [], total: 0, page, size };
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.complaintsByCustomer(customerId)}?${params.toString()}`,
    SERVICE_CODES.complaintsByCustomer
  );

  const items = unwrapList(response).map(mapComplaintDetail).filter((t) => t.id);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;

  return { items, total, page, size };
}

/**
 * Loads a single complaint for manager/technician ticket views.
 * GET /saas/fsm/customer-complaints/{complaintId}
 * FSM03S
 */
export async function fetchComplaintDetail(complaintId) {
  const targetId = complaintId != null ? String(complaintId) : "";
  if (!targetId) return null;

  const response = await fsmGet(
    ENDPOINTS.complaintById(targetId),
    SERVICE_CODES.complaintById
  );

  const item = unwrapItem(response);
  if (!item || typeof item !== "object") return null;

  return mapComplaintDetail(item);
}

/**
 * GET /saas/fsm/customer-complaints/technician/{technicianId}
 * FSM03S
 */
export async function searchComplaintsByTechnician(
  technicianId,
  { page = 0, size = 10, sortBy = "complaintId", sortDir = "DESC" } = {}
) {
  if (technicianId == null || technicianId === "") {
    return { items: [], total: 0, page, size };
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.complaintsByTechnician(technicianId)}?${params.toString()}`,
    SERVICE_CODES.complaintsByTechnician
  );

  const items = unwrapList(response).map(mapComplaintTicket).filter((t) => t.id);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;

  return { items, total, page, size };
}

/**
 * GET /saas/fsm/customer-complaints/status/{complaintStatus}
 * FSM03S
 */
export async function searchComplaintsByStatus(
  complaintStatus,
  { page = 0, size = 10, sortBy = "complaintId", sortDir = "DESC" } = {}
) {
  const status = String(complaintStatus || "").trim();
  if (!status) {
    return { items: [], total: 0, page, size, hasMore: false, last: true };
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.complaintsByStatus(status)}?${params.toString()}`,
    SERVICE_CODES.complaintsByStatus
  );

  const items = unwrapList(response).map(mapComplaintTicket).filter((t) => t.id);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;
  const totalPages = Number(
    response?.data?.totalPages ??
      response?.totalPages ??
      Math.ceil(total / Math.max(size, 1))
  );
  const last =
    response?.data?.last ??
    response?.last ??
    (Number.isFinite(totalPages) ? page >= Math.max(totalPages - 1, 0) : items.length < size);

  return {
    items,
    total,
    page,
    size,
    totalPages: Number.isFinite(totalPages) ? totalPages : undefined,
    last: Boolean(last),
    hasMore: !Boolean(last),
  };
}

/**
 * GET /saas/fsm/customer-complaints/search
 * FSM03S
 */
export async function searchCustomerComplaints({
  page = 0,
  size = 10,
  sortBy = "complaintId",
  sortDir = "DESC",
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.searchTickets}?${params.toString()}`,
    SERVICE_CODES.searchTickets
  );

  const items = unwrapList(response).map(mapComplaintTicket).filter((t) => t.id);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;
  const totalPages = Number(
    response?.data?.totalPages ??
      response?.totalPages ??
      Math.ceil(total / Math.max(size, 1))
  );
  const last =
    response?.data?.last ??
    response?.last ??
    (Number.isFinite(totalPages) ? page >= Math.max(totalPages - 1, 0) : items.length < size);

  return {
    items,
    total,
    page,
    size,
    totalPages: Number.isFinite(totalPages) ? totalPages : undefined,
    last: Boolean(last),
    hasMore: !Boolean(last),
  };
}

/**
 * Maps a complaint-log row from GET /saas/fsm/complaint-log/complaint/{id}.
 */
export function mapComplaintLog(item) {
  return {
    logId: item?.logId ?? null,
    complaintId: item?.complaintId ?? null,
    action: item?.action ?? "",
    oldStatus: item?.oldStatus ?? "",
    newStatus: item?.newStatus ?? "",
    actorId: item?.actorId ?? null,
    actorRole: item?.actorRole ?? "",
    assignedFrom: item?.assignedFrom ?? null,
    assignedTo: item?.assignedTo ?? null,
    createdOn: item?.createdOn ?? "",
    notes: item?.notes ?? null,
  };
}

/**
 * GET /saas/fsm/complaint-log/complaint/{complaintId}?page=&size=
 * FSM03S
 */
export async function fetchComplaintLogs(
  complaintId,
  { page = 0, size = 20 } = {}
) {
  if (complaintId == null || complaintId === "") {
    return { items: [], total: 0, page, size, last: true };
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  const response = await fsmGet(
    `${ENDPOINTS.complaintLog(complaintId)}?${params.toString()}`,
    SERVICE_CODES.complaintLog
  );

  const items = unwrapList(response).map(mapComplaintLog);
  const data = response?.data ?? {};
  const total =
    Number(data.totalElements ?? response?.totalElements ?? items.length) ||
    items.length;
  const currentPage = Number(data.pageNumber ?? page);
  const resolvedPage = Number.isFinite(currentPage) ? currentPage : page;
  const totalPages = Number(data.totalPages);
  const last =
    typeof data.last === "boolean"
      ? data.last
      : Number.isFinite(totalPages)
        ? resolvedPage >= totalPages - 1
        : items.length < size;

  return {
    items,
    total,
    page: resolvedPage,
    size: Number(data.pageSize ?? size) || size,
    last,
  };
}

// ---------------------------------------------------------------------------
// Complaint checklist items (templates)
// ---------------------------------------------------------------------------

export const CHECKLIST_WHO_TO_CHECK = ["Internal Asset", "Field Service"];

/**
 * Returns which checklist types (whoToCheck) already have a template for the
 * given complaint type — i.e. at least one item exists for the pair:
 *   (complaintTypeId, whoToCheck)
 *
 * Uses:
 *   GET /products-complaints-checklist/complaint/{complaintTypeId}/search/whotocheck
 *     ?text=Internal Asset|Field Service
 * for each option, so we never mark a type as taken based on another complaint.
 */
export async function getExistingWhoToCheckForComplaint(complaintTypeId) {
  if (complaintTypeId == null || complaintTypeId === "") {
    return [];
  }

  const target = String(complaintTypeId);
  const results = await Promise.all(
    CHECKLIST_WHO_TO_CHECK.map(async (whoToCheck) => {
      try {
        const items = await fetchAllChecklistItemsByWhoToCheck(
          complaintTypeId,
          whoToCheck
        );
        const belongs = items.filter(
          (item) =>
            item.complaintTypeId == null ||
            String(item.complaintTypeId) === target
        );
        return belongs.length > 0 ? whoToCheck : null;
      } catch (err) {
        console.error(
          `Failed to check existing template for complaint ${target} / ${whoToCheck}:`,
          err
        );
        return null;
      }
    })
  );

  return results.filter(Boolean);
}

/**
 * Loads checklist items for one template key: complaintTypeId + whoToCheck.
 * Filters defensively so items from other complaint types are excluded.
 */
export async function fetchChecklistTemplateItems(complaintTypeId, whoToCheck) {
  if (complaintTypeId == null || complaintTypeId === "" || !whoToCheck) {
    return [];
  }

  const targetComplaint = String(complaintTypeId);
  const targetWho = String(whoToCheck).trim().toLowerCase();

  const items = await fetchAllChecklistItemsByWhoToCheck(
    complaintTypeId,
    whoToCheck
  );

  return items.filter((item) => {
    const sameComplaint =
      item.complaintTypeId == null ||
      String(item.complaintTypeId) === targetComplaint;
    const sameWho =
      String(item.whoToCheck || "")
        .trim()
        .toLowerCase() === targetWho;
    return sameComplaint && sameWho;
  });
}

/**
 * Normalizes checklist API payloads: list, paged content, or a single item object.
 */
function unwrapChecklistPayload(response) {
  const list = unwrapList(response);
  if (list.length > 0) return list;

  const data = response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    // Single checklist item: { checklistId, complaintTypeId, whatToCheck, ... }
    if (
      data.checklistId != null ||
      data.checklistItemId != null ||
      data.whatToCheck != null ||
      data.whoToCheck != null
    ) {
      return [data];
    }
    if (Array.isArray(data.content)) return data.content;
    if (Array.isArray(data.items)) return data.items;
  }
  return [];
}

/**
 * Maps a products-complaints-checklist row.
 */
export function mapChecklistItem(item) {
  const id =
    item?.checklistId ??
    item?.checklistItemId ??
    item?.complaintChecklistId ??
    item?.id ??
    null;
  const complaintTypeId =
    item?.complaintTypeId ??
    item?.complaint?.complaintTypeId ??
    null;
  const whoToCheck =
    item?.whoToCheck ??
    item?.checkedByRole ??
    item?.checklistType ??
    "";
  const whatToCheck =
    item?.whatToCheck ??
    item?.checklistItem ??
    item?.step ??
    item?.particular ??
    "";

  return {
    id: id != null ? String(id) : "",
    checklistItemId: id,
    checklistId: id,
    complaintTypeId,
    complaintTypeName:
      item?.complaintTypeName ??
      item?.complaintType ??
      item?.complaint?.complaintTypeName ??
      (complaintTypeId != null ? `Complaint #${complaintTypeId}` : "—"),
    productServiceId:
      item?.productServiceId ??
      item?.productService?.productServiceId ??
      null,
    systemName:
      item?.productServiceName ??
      item?.systemName ??
      item?.productService?.productServiceName ??
      (item?.productServiceId != null ? String(item.productServiceId) : "—"),
    whoToCheck,
    whatToCheck,
    sequenceOrder: Number(item?.sequenceOrder ?? item?.sequence ?? 0) || 0,
    isMandatory: item?.isMandatory === true,
    isActive: item?.isActive !== false,
    raw: item,
  };
}

/**
 * Loads checklist items for a complaint type filtered by whoToCheck.
 * GET /saas/fsm/products-complaints-checklist/complaint/{complaintTypeId}/search/whotocheck
 *   ?text=Internal Asset|Field Service&page=0&size=10&sortBy=sequenceOrder&sortDir=ASC
 * FSM01S
 */
export async function fetchChecklistItemsByWhoToCheck(
  complaintTypeId,
  whoToCheck,
  { page = 0, size = 10, sortBy = "sequenceOrder", sortDir = "ASC" } = {}
) {
  if (complaintTypeId == null || complaintTypeId === "") {
    return { items: [], total: 0, page, size };
  }
  if (!whoToCheck) {
    return { items: [], total: 0, page, size };
  }

  const params = new URLSearchParams({
    text: String(whoToCheck),
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.checklistByWhoToCheck(complaintTypeId)}?${params.toString()}`,
    SERVICE_CODES.searchChecklistItems
  );

  const items = unwrapChecklistPayload(response)
    .map(mapChecklistItem)
    .filter((i) => i.id && i.isActive !== false);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;

  return { items, total, page, size };
}

/**
 * Loads all checklist pages for a complaint type + whoToCheck value.
 */
export async function fetchAllChecklistItemsByWhoToCheck(
  complaintTypeId,
  whoToCheck
) {
  if (complaintTypeId == null || complaintTypeId === "" || !whoToCheck) {
    return [];
  }

  const matched = [];
  let page = 0;
  const size = 10;
  let totalPages = 1;

  while (page < totalPages) {
    const { items, total } = await fetchChecklistItemsByWhoToCheck(
      complaintTypeId,
      whoToCheck,
      { page, size, sortBy: "sequenceOrder", sortDir: "ASC" }
    );
    matched.push(...items);
    totalPages = Math.max(1, Math.ceil(total / size));
    if (items.length === 0) break;
    page += 1;
    if (page > 40) break;
  }

  return matched.sort(
    (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
  );
}

/**
 * Loads Internal Asset + Field Service checklists for a complaint type
 * via the whoToCheck search API (two concurrent requests).
 */
export async function fetchChecklistItemsSplitByWho(complaintTypeId) {
  if (complaintTypeId == null || complaintTypeId === "") {
    return { internal: [], field: [] };
  }

  const [internal, field] = await Promise.all([
    fetchAllChecklistItemsByWhoToCheck(complaintTypeId, "Internal Asset"),
    fetchAllChecklistItemsByWhoToCheck(complaintTypeId, "Field Service"),
  ]);

  return { internal, field };
}

/**
 * Loads checklist items for a complaint type.
 * GET /saas/fsm/products-complaints-checklist/complaint/{complaintTypeId}
 *   ?page=0&size=10&sortBy=sequenceOrder&sortDir=ASC
 * FSM01S
 */
export async function fetchChecklistItemsByComplaintType(complaintTypeId) {
  if (complaintTypeId == null || complaintTypeId === "") {
    return [];
  }

  const target = String(complaintTypeId);
  const matched = [];
  let page = 0;
  const size = 10;
  let totalPages = 1;

  while (page < totalPages) {
    const params = new URLSearchParams({
      page: String(page),
      size: String(size),
      sortBy: "sequenceOrder",
      sortDir: "ASC",
    });

    const response = await fsmGet(
      `${ENDPOINTS.checklistByComplaintType(target)}?${params.toString()}`,
      SERVICE_CODES.searchChecklistItems
    );

    const items = unwrapChecklistPayload(response)
      .map(mapChecklistItem)
      .filter((i) => i.id && i.isActive !== false);

    matched.push(...items);

    const total =
      Number(
        response?.data?.totalElements ??
          response?.totalElements ??
          response?.data?.total ??
          response?.total ??
          0
      ) || 0;

    if (total > 0) {
      totalPages = Math.max(1, Math.ceil(total / size));
    } else if (items.length < size) {
      // No reliable total — stop when a short page is returned
      break;
    } else {
      totalPages = page + 2; // keep going while pages are full
    }

    if (items.length === 0) break;
    page += 1;
    if (page > 100) break;
  }

  // Deduplicate by checklist id (overlapping pages / unstable totals)
  const byId = new Map();
  for (const item of matched) {
    byId.set(String(item.checklistId ?? item.id), item);
  }

  return [...byId.values()].sort(
    (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0)
  );
}

/**
 * Loads a single page of checklist items for a complaint (used by the
 * Checklist Submissions list page).
 * GET /saas/fsm/products-complaints-checklist/complaint/{complaintId}
 *   ?page=&size=&sortBy=sequenceOrder&sortDir=ASC
 * FSM01S
 */
export async function searchChecklistItemsByComplaint({
  complaintId,
  page = 0,
  size = 10,
  sortBy = "sequenceOrder",
  sortDir = "ASC",
} = {}) {
  if (complaintId == null || complaintId === "") {
    return { items: [], total: 0, page, size };
  }

  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });

  const response = await fsmGet(
    `${ENDPOINTS.checklistByComplaintType(complaintId)}?${params.toString()}`,
    SERVICE_CODES.searchChecklistItems
  );

  const items = unwrapChecklistPayload(response)
    .map(mapChecklistItem)
    .filter((i) => i.id && i.isActive !== false);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;

  return { items, total, page, size };
}

/**
 * Splits checklist items into Internal Asset vs Field Service buckets.
 */
export function splitChecklistByWho(items = []) {
  const internal = [];
  const field = [];

  for (const item of items) {
    const key = String(item?.whoToCheck || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ");
    if (key.includes("internal")) {
      internal.push(item);
    } else if (key.includes("field")) {
      field.push(item);
    } else {
      // Unknown whoToCheck — keep under field so tech can still see actionable items
      field.push(item);
    }
  }

  const bySeq = (a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0);
  return {
    internal: [...internal].sort(bySeq),
    field: [...field].sort(bySeq),
  };
}

/**
 * Reads checklist submission rows from a complaint detail payload.
 * GET /customer-complaints/{id} returns `checklistItems`; update payloads use
 * `complaintChecklist` — accept both.
 */
export function getComplaintChecklistSubmissions(ticketOrRaw) {
  const raw = ticketOrRaw?.raw && typeof ticketOrRaw.raw === "object"
    ? ticketOrRaw.raw
    : ticketOrRaw;
  if (!raw || typeof raw !== "object") return [];

  if (Array.isArray(raw.checklistItems) && raw.checklistItems.length > 0) {
    return raw.checklistItems;
  }
  if (Array.isArray(raw.complaintChecklist) && raw.complaintChecklist.length > 0) {
    return raw.complaintChecklist;
  }
  if (Array.isArray(ticketOrRaw?.checklistItems) && ticketOrRaw.checklistItems.length > 0) {
    return ticketOrRaw.checklistItems;
  }
  return [];
}

/**
 * Picks the latest checklist submission for a template item.
 * API may return multiple rows per checklistItemId (re-saves append); prefer
 * highest recordId, then latest checkedAt. Optionally scope by whoToCheck
 * (stored in remarks on complaint checklist rows).
 */
export function findLatestChecklistSubmission(
  submissions,
  checklistItemId,
  whoToCheck
) {
  if (!Array.isArray(submissions) || submissions.length === 0) return null;
  if (checklistItemId == null || checklistItemId === "") return null;

  const id = String(checklistItemId);
  const who = String(whoToCheck || "")
    .trim()
    .toLowerCase();

  const matches = submissions.filter((entry) => {
    if (String(entry?.checklistItemId ?? entry?.id ?? "") !== id) return false;
    if (!who) return true;
    const entryWho = String(entry?.remarks ?? entry?.whoToCheck ?? "")
      .trim()
      .toLowerCase();
    if (!entryWho) return true;
    return entryWho === who;
  });

  if (matches.length === 0) return null;

  return matches.reduce((best, cur) => {
    const bestRid = Number(best?.recordId) || 0;
    const curRid = Number(cur?.recordId) || 0;
    if (curRid !== bestRid) return curRid > bestRid ? cur : best;
    const bestAt = Date.parse(best?.checkedAt || "") || 0;
    const curAt = Date.parse(cur?.checkedAt || "") || 0;
    return curAt >= bestAt ? cur : best;
  });
}

const CHECKLIST_STATUS_LABELS = [
  "Pending",
  "In Progress",
  "Completed",
  "Not Applicable",
];

/**
 * Preferred status value from a checklist submission row.
 * Detail API uses `status`. `remarks` now holds whoToCheck (e.g. "Internal Asset"),
 * so only treat remarks as status when it matches a known status label (legacy).
 */
export function getChecklistSubmissionStatus(entry) {
  if (!entry || typeof entry !== "object") return "";

  const direct =
    entry.status ?? entry.checkedStatus ?? entry.checklistStatus ?? "";
  if (direct != null && String(direct).trim() !== "") {
    return String(direct).trim();
  }

  const remarks = String(entry.remarks ?? "").trim();
  if (
    remarks &&
    CHECKLIST_STATUS_LABELS.some(
      (label) => label.toLowerCase() === remarks.toLowerCase()
    )
  ) {
    return remarks;
  }

  return "";
}

/**
 * Formats checkedAt as local datetime without timezone suffix:
 * e.g. "2026-07-30T17:39:30"
 */
function formatChecklistCheckedAt(value) {
  const date = value instanceof Date ? value : value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) {
    return formatChecklistCheckedAt(new Date());
  }
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * Builds complaintChecklist entry for update payload.
 * remarks = whoToCheck from checklist template API (e.g. "Internal Asset").
 * status = technician-selected checklist status (e.g. "In Progress").
 */
export function buildComplaintChecklistEntry({
  recordId,
  complaintId,
  checklistItemId,
  checkedByRole = "Technician",
  checkedBy,
  isChecked = true,
  remarks = "",
  status,
  checkedAt,
}) {
  const toId = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const statusValue = String(status ?? "").trim();
  const entry = {
    complaintId: toId(complaintId),
    checklistItemId: toId(checklistItemId),
    checkedByRole: String(checkedByRole || "Technician"),
    checkedBy: toId(checkedBy ?? sessionDB.getEmpId()),
    isChecked: Boolean(isChecked),
    status: statusValue,
    remarks: String(remarks ?? "").trim(),
    checkedAt: formatChecklistCheckedAt(checkedAt),
  };

  const resolvedRecordId = toId(recordId);
  if (resolvedRecordId > 0) {
    entry.recordId = resolvedRecordId;
  }

  return entry;
}

/**
 * GET /saas/fsm/products-complaints-checklist/search
 * FSM01S
 *
 * Pass `complaintTypeId` to scope results to a single complaint type — this is
 * required by the Checklist Template Creation/Management flow, which must not
 * load checklist items until a complaint type has been selected.
 */
export async function searchChecklistItems({
  page = 0,
  size = 10,
  sortBy = "sequenceOrder",
  sortDir = "ASC",
  complaintTypeId,
} = {}) {
  const params = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortDir,
  });
  if (complaintTypeId != null && complaintTypeId !== "") {
    params.set("complaintTypeId", String(complaintTypeId));
  }

  const response = await fsmGet(
    `${ENDPOINTS.searchChecklistItems}?${params.toString()}`,
    SERVICE_CODES.searchChecklistItems
  );

  const items = unwrapChecklistPayload(response).map(mapChecklistItem).filter((i) => i.id);
  const total =
    Number(
      response?.data?.totalElements ??
        response?.totalElements ??
        response?.data?.total ??
        response?.total ??
        items.length
    ) || items.length;

  return { items, total, page, size };
}

/**
 * GET /saas/fsm/products-complaints-checklist/{checklistId}
 * FSM01S — single checklist item for edit/view.
 */
export async function fetchChecklistItemById(checklistId) {
  if (checklistId == null || checklistId === "") {
    return null;
  }

  const response = await fsmGet(
    ENDPOINTS.checklistById(checklistId),
    SERVICE_CODES.searchChecklistItems
  );

  const items = unwrapChecklistPayload(response).map(mapChecklistItem).filter((i) => i.id);
  if (items.length > 0) return items[0];

  const item = unwrapItem(response);
  if (!item || typeof item !== "object") return null;
  const mapped = mapChecklistItem(item);
  return mapped.id ? mapped : null;
}

/**
 * POST /saas/fsm/products-complaints-checklist/add
 * FSM01A
 */
export async function createChecklistItem(payload) {
  const response = await fsmPost(
    ENDPOINTS.addChecklistItem,
    payload,
    SERVICE_CODES.addChecklistItem
  );
  return unwrapItem(response) ?? response;
}

/**
 * PUT /saas/fsm/products-complaints-checklist/update/{id}
 * FSM01U
 */
export async function updateChecklistItem(id, payload) {
  if (id == null || id === "") {
    throw new Error("Checklist item ID is required.");
  }
  const response = await fsmPut(
    ENDPOINTS.updateChecklistItem(id),
    payload,
    SERVICE_CODES.updateChecklistItem
  );
  return unwrapItem(response) ?? response;
}

/**
 * DELETE /saas/fsm/products-complaints-checklist/delete/{id}
 * FSM01D
 */
export async function deleteChecklistItem(id) {
  if (id == null || id === "") {
    throw new Error("Checklist item ID is required.");
  }
  const response = await fsmDel(
    ENDPOINTS.deleteChecklistItem(id),
    SERVICE_CODES.deleteChecklistItem
  );
  return unwrapItem(response) ?? response;
}

/**
 * Deletes every checklist item for a complaint type by calling
 * DELETE /products-complaints-checklist/delete/{checklistId} for each item.
 */
export async function deleteChecklistTemplateByComplaintType(complaintTypeId) {
  if (complaintTypeId == null || complaintTypeId === "") {
    throw new Error("Complaint type ID is required.");
  }

  const items = await fetchChecklistItemsByComplaintType(complaintTypeId);
  if (items.length === 0) return { deleted: 0 };

  await Promise.all(
    items.map((item) => deleteChecklistItem(item.checklistId ?? item.id))
  );

  return { deleted: items.length };
}

/**
 * Deletes one Checklist Template (all items sharing complaintTypeId + whoToCheck).
 */
export async function deleteChecklistTemplate({
  complaintTypeId,
  whoToCheck,
  checklistItems,
} = {}) {
  if (complaintTypeId == null || complaintTypeId === "") {
    throw new Error("Complaint type ID is required.");
  }
  if (!whoToCheck) {
    throw new Error("Checklist type (whoToCheck) is required.");
  }

  let items = Array.isArray(checklistItems) ? checklistItems : null;
  if (!items || items.length === 0) {
    const all = await fetchAllChecklistItems({ complaintTypeId });
    const target = String(whoToCheck).trim().toLowerCase();
    items = all.filter(
      (item) => String(item.whoToCheck || "").trim().toLowerCase() === target
    );
  }

  if (items.length === 0) return { deleted: 0 };

  await Promise.all(
    items.map((item) => deleteChecklistItem(item.checklistId ?? item.id))
  );

  return { deleted: items.length };
}

export function buildChecklistItemPayload({
  complaintTypeId,
  whatToCheck,
  whoToCheck,
  sequenceOrder = 1,
  isMandatory,
  isActive,
}) {
  const toId = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const payload = {
    complaintTypeId: toId(complaintTypeId),
    whatToCheck: String(whatToCheck ?? "").trim(),
    whoToCheck: String(whoToCheck ?? "").trim(),
    sequenceOrder: Number(sequenceOrder) || 1,
  };

  if (typeof isMandatory === "boolean") {
    payload.isMandatory = isMandatory;
  }
  if (typeof isActive === "boolean") {
    payload.isActive = isActive;
  }

  return payload;
}

// ---------------------------------------------------------------------------
// Products & Services configuration (FSM01)
// ---------------------------------------------------------------------------

const PRODUCT_SERVICES = "/products-services";
const productServiceQuery = (params) => new URLSearchParams(params).toString();

/**
 * GET /search, /search/name or /search/status — FSM01S.
 * The API filters by name or by status, not both, so `name` takes precedence.
 */
export async function listProductServices({ name = "", isActive, page = 0, size = 10 } = {}) {
  const path = name
    ? `${PRODUCT_SERVICES}/search/name?${productServiceQuery({ name, page, size })}`
    : isActive !== undefined
      ? `${PRODUCT_SERVICES}/search/status?${productServiceQuery({ isActive, page, size })}`
      : `${PRODUCT_SERVICES}/search?${productServiceQuery({ page, size })}`;
  return normalizePageResponse(await fsmGet(path, "FSM01S"), { page, size });
}

/** GET /{productServiceId} — FSM01S */
export async function getProductService(productServiceId) {
  return unwrapItem(await fsmGet(`${PRODUCT_SERVICES}/${productServiceId}`, "FSM01S"));
}

/** POST /add — FSM01A */
export async function addProductService(payload) {
  return unwrapItem(await fsmPost(`${PRODUCT_SERVICES}/add`, payload, "FSM01A"));
}

/** PUT /update/{productServiceId} — FSM01U */
export async function updateProductService(productServiceId, payload) {
  return unwrapItem(await fsmPut(`${PRODUCT_SERVICES}/update/${productServiceId}`, payload, "FSM01U"));
}

/** PATCH /update/status/{productServiceId} — FSM01U, body { isActive } */
export async function updateProductServiceStatus(productServiceId, isActive) {
  return unwrapItem(await fsmPatch(`${PRODUCT_SERVICES}/update/status/${productServiceId}`, { isActive }, "FSM01U"));
}

// ---------------------------------------------------------------------------
// Complaint types per product/service (FSM01)
// ---------------------------------------------------------------------------

const PRODUCT_COMPLAINTS = "/products-complaints";

/**
 * GET /product/{id}, /product/{id}/search/type or /product/{id}/status — FSM01S.
 * The API filters by type name or by status, not both, so `type` takes precedence.
 */
export async function listProductComplaintTypes({ productServiceId, type = "", isActive, page = 0, size = 10 }) {
  const base = `${PRODUCT_COMPLAINTS}/product/${productServiceId}`;
  const path = type
    ? `${base}/search/type?${productServiceQuery({ type, page, size })}`
    : isActive !== undefined
      ? `${base}/status?${productServiceQuery({ isActive, page, size })}`
      : `${base}?${productServiceQuery({ page, size })}`;
  return normalizePageResponse(await fsmGet(path, "FSM01S"), { page, size });
}

/** GET /{complaintTypeId} — FSM01S */
export async function getProductComplaintType(complaintTypeId) {
  return unwrapItem(await fsmGet(`${PRODUCT_COMPLAINTS}/${complaintTypeId}`, "FSM01S"));
}

/** POST /add — FSM01A */
export async function addProductComplaintType(payload) {
  return unwrapItem(await fsmPost(`${PRODUCT_COMPLAINTS}/add`, payload, "FSM01A"));
}

/** PUT /update/{complaintTypeId} — FSM01U */
export async function updateProductComplaintType(complaintTypeId, payload) {
  return unwrapItem(await fsmPut(`${PRODUCT_COMPLAINTS}/update/${complaintTypeId}`, payload, "FSM01U"));
}

/** PATCH /update/status/{complaintTypeId} — FSM01U, body { isActive } */
export async function updateProductComplaintTypeStatus(complaintTypeId, isActive) {
  return unwrapItem(await fsmPatch(`${PRODUCT_COMPLAINTS}/update/status/${complaintTypeId}`, { isActive }, "FSM01U"));
}
